import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useApi } from "../../hooks/useApi";
import { format } from "date-fns";

const ClientOverview = ({ user, setSelectedTab }) => {
  const api = useApi();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api("/bookings/bookings.php", { method: "GET" });
        const all = res.data.bookings || [];
        const upcoming = all
          .filter((b) => b.status === "confirmed")
          .filter((b) => new Date(b.starts_at) > new Date())
          // API returns bookings DESC (farthest first); sort soonest-first
          // before slicing so Overview shows the 3 NEXT appointments.
          .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))
          .slice(0, 3);
        setBookings(upcoming);
      } catch (e) {
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <Box sx={{ p: { xs: 3, md: 5 }, maxWidth: "900px" }}>
      <Typography variant="h3" sx={{ mb: 0.5 }}>
        Welcome back, {user?.first_name}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
        Ready for your next cut?
      </Typography>

      {/* Book CTA */}
      <Box
        component={Paper}
        elevation={1}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2,
          border: "0.1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Book your next appointment
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Choose your barber, service, and time.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setSelectedTab("Book Appointment")}
        >
          Book Now
        </Button>
      </Box>

      {/* Upcoming */}
      <Typography variant="overline" sx={{ mb: 1.5, display: "block" }}>
        Upcoming
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
          <CircularProgress size={16} sx={{ color: "text.secondary" }} />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Loading appointments...
          </Typography>
        </Box>
      ) : bookings.length === 0 ? (
        <Box
          component={Paper}
          elevation={1}
          sx={{
            p: 3,
            borderRadius: 2,
            border: "0.1px solid",
            borderColor: "customDivider",
            backgroundColor: "offWhite",
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No upcoming appointments. Book one now!
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {bookings.map((b) => (
            <Box
              key={b.id}
              component={Paper}
              elevation={1}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "0.1px solid",
                borderColor: "customDivider",
                backgroundColor: "offWhite",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {b.service?.name}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  with {b.barber?.first_name} {b.barber?.last_name}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.third", fontWeight: 600 }}
                >
                  {format(
                    new Date(b.starts_at_local || b.starts_at),
                    "EEE, MMM d",
                  )}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {format(new Date(b.starts_at_local || b.starts_at), "h:mm a")}
                </Typography>
              </Box>
            </Box>
          ))}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
            <Typography
              variant="body2"
              onClick={() => setSelectedTab("Appointments")}
              sx={{
                color: "text.third",
                fontWeight: 500,
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                  textUnderlineOffset: "4px",
                },
              }}
            >
              View all appointments →
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ClientOverview;
